import { inject } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
	AccountInfo,
	AuthenticationResult,
	AuthError,
	EventType,
	InteractionRequiredAuthError,
	InteractionStatus,
} from '@azure/msal-browser';
import { EMPTY, filter, firstValueFrom, from, lastValueFrom, of, switchMap, take } from 'rxjs';
import { SessionManagerService } from '../../shared/storage/session-manager.service';
import { decodeJwtPayload } from '../../shared/helpers/jwt-decoder.helper';
import { environment } from '../../../environments/environment';
import { BrandingService } from '../services/branding.service';
import { TENANT_FEATURES, TenantFeatureProvider } from '../services/tenant-feature.service';
import { CurrentUserService } from '../services/current-user.service';

interface KlifdAccountContext {
	account: AccountInfo;
	claims: Record<string, unknown>;
}

export const initializeAuth = async () => {
	const auth = inject(MsalService);
	const broadcast = inject(MsalBroadcastService);
	const sessionManager = inject(SessionManagerService);
	const brandingService = inject(BrandingService);
	const tenantFeatureService = inject(TENANT_FEATURES);
	const currentUserService = inject(CurrentUserService);

	await lastValueFrom(auth.initialize());
	// Redirect processing owns the MSAL interaction lock. Tokens and permissions must wait for it.
	const response = await lastValueFrom(auth.handleRedirectObservable());
	await firstValueFrom(broadcast.inProgress$.pipe(filter((status) => status === InteractionStatus.None)));

	if (response?.account) {
		auth.instance.setActiveAccount(response.account);
		currentUserService.clear();
	}

	const context = response?.account
		? await getRedirectAccountContext(auth, response)
		: await getKlifdAccountContext(auth);

	if (context) {
		auth.instance.setActiveAccount(context.account);
		applyTenantFromClaims(brandingService, tenantFeatureService, context.claims);
		sessionManager.startSession(context.account.localAccountId);
	} else if (auth.instance.getAllAccounts().length > 0) {
		console.warn('[MSAL] Cached accounts found, but no valid KLIFD account could be restored.');

		sessionManager.invalidate({ reason: 'auth-loss' });
		await redirectToAccountSelection(auth);

		return;
	}

	registerEventCallbacks(auth, broadcast);
};

const getRedirectAccountContext = async (
	auth: MsalService,
	response: AuthenticationResult,
): Promise<KlifdAccountContext> => {
	// Default OIDC login scopes can return a token for another resource, not the KLIFD API.
	const hasApiScopes =
		environment.auth.scopes.length > 0 &&
		environment.auth.scopes.every((scope) =>
			response.scopes.some((grantedScope) => grantedScope.toLowerCase() === scope.toLowerCase()),
		);
	let claims: Record<string, unknown>;
	try {
		claims =
			response.accessToken && hasApiScopes
				? decodeJwtPayload(response.accessToken)
				: await getKlifdClaims(auth, response.account);
	} catch (error) {
		if (requiresInteraction(error)) {
			throw new AuthError('interaction_required', 'KLIFD login could not establish an application session.');
		}
		if (
			error instanceof SyntaxError ||
			(typeof error === 'object' && error !== null && 'name' in error && error.name === 'InvalidCharacterError')
		) {
			throw new AuthError('invalid_token', 'KLIFD login returned an unreadable API token.');
		}
		throw error;
	}

	const errorCode = getKlifdClaimsError(claims);
	if (errorCode) {
		throw new AuthError(errorCode, 'KLIFD login could not establish an application session.');
	}

	return { account: response.account, claims };
};

const registerEventCallbacks = (auth: MsalService, broadcast: MsalBroadcastService) => {
	let reauthentication: Promise<void> | undefined;

	auth.instance.addEventCallback((event) => {
		if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
			const error = event.error;

			if (requiresInteraction(error)) {
				// Token failures can arrive concurrently and from our own retry. Only one may start a login.
				reauthentication ??= Promise.resolve()
					.then(() =>
						firstValueFrom(
							broadcast.inProgress$.pipe(
								// Discard stale token checks if another interaction starts before they finish.
								switchMap((status) => {
									if (status !== InteractionStatus.None) {
										return EMPTY;
									}

									// Recovery must not silently switch the application's user or tenant.
									const account = auth.instance.getActiveAccount();
									return account ? from(tryGetValidKlifdAccountContext(auth, account)) : of(null);
								}),
								take(1),
								switchMap((context) => {
									if (context) {
										reauthentication = undefined;
										return of(undefined);
									}

									console.warn('[MSAL] Interaction required. Redirecting to account selection.');
									// Keep the promise until navigation so late failures cannot start another redirect.
									return from(redirectToAccountSelection(auth));
								}),
							),
						),
					)
					.catch((error: unknown) => {
						reauthentication = undefined;
						console.warn('[MSAL] Could not reauthenticate.', error);
					});

				return;
			}

			console.warn('[MSAL][ACQUIRE_TOKEN_FAILURE]', {
				error,
				url: window.location.href,
				timestamp: new Date().toISOString(),
			});
		}
	});
};

const redirectToAccountSelection = async (auth: MsalService): Promise<void> => {
	await lastValueFrom(
		auth.loginRedirect({
			scopes: environment.auth.scopes,
			prompt: 'select_account',
		}),
	);
};

const requiresInteraction = (error: unknown): boolean =>
	error instanceof InteractionRequiredAuthError ||
	(error instanceof AuthError && error.errorCode === 'interaction_required');

const getKlifdAccountContext = async (auth: MsalService): Promise<KlifdAccountContext | null> => {
	const activeAccount = auth.instance.getActiveAccount();

	if (activeAccount) {
		const context = await tryGetValidKlifdAccountContext(auth, activeAccount);

		if (context) {
			return context;
		}
	}

	const accounts = auth.instance.getAllAccounts();

	if (accounts.length === 0) {
		return null;
	}

	const validContexts: KlifdAccountContext[] = [];

	for (const account of accounts) {
		const context = await tryGetValidKlifdAccountContext(auth, account);

		if (context) {
			validContexts.push(context);
		}
	}

	if (validContexts.length === 1) {
		return validContexts[0];
	}

	if (validContexts.length > 1) {
		console.warn('[MSAL] Multiple valid KLIFD accounts found. User must select an account.');
		return null;
	}

	console.warn('[MSAL] No valid KLIFD account found in cache.');

	return null;
};

const tryGetValidKlifdAccountContext = async (
	auth: MsalService,
	account: AccountInfo,
): Promise<KlifdAccountContext | null> => {
	try {
		const claims = await getKlifdClaims(auth, account);

		if (getKlifdClaimsError(claims)) {
			return null;
		}

		return {
			account,
			claims,
		};
	} catch (error) {
		if (requiresInteraction(error)) {
			return null;
		}

		throw error;
	}
};

const getKlifdClaims = async (auth: MsalService, account: AccountInfo): Promise<Record<string, unknown>> => {
	const response = await auth.instance.acquireTokenSilent({
		account,
		scopes: environment.auth.scopes,
	});

	return decodeJwtPayload(response.accessToken);
};

const getKlifdClaimsError = (
	claims: Record<string, unknown>,
): 'invalid_token' | 'invalid_audience' | 'missing_tenant' | null => {
	if (!claims || typeof claims !== 'object' || Array.isArray(claims)) {
		return 'invalid_token';
	}
	if (claims['aud'] !== environment.auth.clientId) {
		return 'invalid_audience';
	}
	if (!getTenantNameFromClaims(claims)) {
		return 'missing_tenant';
	}

	return null;
};

const applyTenantFromClaims = (
	brandingService: BrandingService,
	tenantFeatureService: TenantFeatureProvider,
	claims: Record<string, unknown>,
): void => {
	const tenantName = getTenantNameFromClaims(claims);

	if (!tenantName) {
		console.warn('[MSAL] Missing tenant name. Branding not applied.');
		brandingService.setTenant('');
		tenantFeatureService.setTenant('');
		return;
	}

	brandingService.setTenant(tenantName);
	tenantFeatureService.setTenant(tenantName);
};

const getTenantNameFromClaims = (claims: Record<string, unknown>): string | undefined => {
	const tenantNameClaim = claims['extn.tenantName'];

	if (Array.isArray(tenantNameClaim)) {
		const [tenantName] = tenantNameClaim;

		return typeof tenantName === 'string' ? tenantName : undefined;
	}

	return typeof tenantNameClaim === 'string' ? tenantNameClaim : undefined;
};
